"use client";

import { useState } from "react";

// Live profit calculator (Base44 `yI`). Money in integer cents; the qty stepper
// steps by MOQ and is clamped to [minOrder, remaining units].
export function ProfitCalculator({
  costCents,
  msrpCents,
  minOrder,
  unitsAvailable,
  spendingPower,
  light = false,
  compact = false,
  qty: controlledQty,
  onQtyChange,
}: {
  costCents: number;
  msrpCents: number;
  minOrder: number;
  unitsAvailable: number;
  spendingPower: number; // cents
  light?: boolean;
  compact?: boolean;
  qty?: number; // controlled quantity; falls back to internal state when omitted
  onQtyChange?: (qty: number) => void;
}) {
  const step = minOrder || 1;
  const maxQty = Math.max(step, unitsAvailable || step);
  // Every quantity that leaves this control is clamped to [MOQ, remaining], so
  // the stepper can never push qty out of range.
  const clampQty = (n: number) =>
    Math.max(step, Math.min(maxQty, Number.isFinite(n) ? n : step));
  // Default to the MOQ, but never above what is actually available.
  const [internalQty, setInternalQty] = useState(Math.min(step, unitsAvailable || step));
  const qty = controlledQty ?? internalQty;
  const setQty = (next: number) => {
    const clamped = clampQty(next);
    setDraft(String(clamped));
    setError(null);
    return onQtyChange ? onQtyChange(clamped) : setInternalQty(clamped);
  };

  // Manual entry: the input keeps a free-text draft so a mid-typing value can be
  // validated before it becomes the live qty. An out-of-range or non-numeric
  // draft surfaces an error and is NOT propagated, so the Take button can never
  // submit a quantity outside [MOQ, remaining].
  const [draft, setDraft] = useState(String(qty));
  const [error, setError] = useState<string | null>(null);

  const onInputChange = (raw: string) => {
    setDraft(raw);
    const trimmed = raw.trim();
    if (trimmed === "") {
      setError("Enter a quantity");
      return;
    }
    const n = Number(trimmed);
    if (!Number.isInteger(n)) {
      setError("Enter a whole number");
      return;
    }
    if (n < step) {
      setError(`Minimum order is ${step.toLocaleString()}`);
      return;
    }
    if (n > maxQty) {
      setError(`Only ${maxQty.toLocaleString()} available`);
      return;
    }
    setError(null);
    if (onQtyChange) onQtyChange(n);
    else setInternalQty(n);
  };

  // On blur, an invalid draft snaps back to the last valid qty so the field
  // never lingers in an errored state.
  const onInputBlur = () => {
    const n = Number(draft.trim());
    if (draft.trim() === "" || !Number.isInteger(n) || n < step || n > maxQty) {
      setDraft(String(qty));
      setError(null);
    }
  };

  const E = light;
  const bMuted = E ? "text-gray-400" : "text-gray-500";
  const stepperBox = E ? "bg-gray-100 border-gray-200" : "bg-black/30 border-white/10";
  const stepperBtn = E ? "text-gray-500 hover:text-black" : "text-gray-400 hover:text-white";
  const qtyText = E ? "text-black" : "text-white";

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs ${bMuted}`}>Quantity</span>
        <div
          className={`flex items-center gap-2 ${stepperBox} rounded-lg border ${
            error ? "!border-red-500" : ""
          }`}
        >
          <button
            type="button"
            onClick={() => setQty(qty - step)}
            className={`w-7 h-7 ${stepperBtn} transition-colors text-lg`}
          >
            −
          </button>
          <input
            type="text"
            inputMode="numeric"
            aria-label="Quantity"
            aria-invalid={error ? true : undefined}
            value={draft}
            onChange={(e) => onInputChange(e.target.value)}
            onBlur={onInputBlur}
            className={`font-bold ${qtyText} text-sm w-12 text-center tabular-nums bg-transparent outline-none focus:outline-none`}
          />
          <button
            type="button"
            onClick={() => setQty(qty + step)}
            className={`w-7 h-7 ${stepperBtn} transition-colors text-lg`}
          >
            +
          </button>
        </div>
      </div>

      {/* Reserve the error line's height so the layout does not jump as the
          message appears and clears. */}
      <p className="text-[11px] text-red-500 text-right min-h-[15px]" role="alert">
        {error}
      </p>
    </div>
  );
}
